import LinearProgress from "@material-ui/core/LinearProgress"
import { makeStyles } from "@mui/styles"
import cloneDeep from "lodash/cloneDeep"
import styles from "public/static/scss/LeadCaptureLayout.module.scss"
import {
  FunctionComponent,
  ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react"
import { Field, Form } from "react-final-form"
// local import
import HeadingIeltsElite from "components/Ielts/IeltsEliteDemo/HeadingIeltsElite"
import {
  demoAliasQueryConstant,
  getFormId,
  ieltsFormIds,
  LeadSources,
} from "components/Ielts/IeltsOnboardingForms/utils"
import CustomInputSelect from "components/Inputs/CustomInputSelect"
import InputField from "components/Inputs/TextField"
import { intakeValueToLabel } from "components/TofuOnbordingForms/dataMappers"
import {
  autonextDecision,
  forceAutonextDecision,
  getDependentQuestionFieldName,
  geturlToRedirect,
  indexCal,
  nameExtraSpacetriming,
  validateHowToHelpVal,
} from "components/TofuOnbordingForms/utils"
import { fireTagEvent } from "ga"
import useDemoAlias from "hooks/ielts/useDemoAlias"
import { State } from "Interfaces/storeSchema"
import { useRouter } from "next/router"
import { navRoutes } from "page_routes"
import { getFilteredFormDataFromQueryObject } from "pages/ielts-preparation-online/ielts-elite-demo"
import { useSelector } from "react-redux"
import {
  trackEventAsync,
  trackIeltsClick,
  trackIeltsFormAsync,
  trackPageV2,
} from "scripts/segment"
import {
  getQueryParamStringFromObject,
  redirectOutFromIframe,
} from "utils/common_utils"
import { handleFormSubmissionGaEventForm } from "utils/event_utils"
import { ieltsPageName } from "utils/events-utils"
import { getUtmData } from "utils/utm_utils"
import SearchDropDown from "./SearchDropDown"

type PageProps = {
  isLoggedIn: undefined | boolean
  loggedInNumber: string | number | null
  formJson: any
  prefilledData?: Record<string, any>
  onSubmitForm?: (val: any) => void
  isModal?: boolean
  formHeading?: string | ReactNode
  buttonCta?: string
  isMcFlow?: boolean
}

const ieltsEliteDemoFormUseStyles = makeStyles({
  root: {
    width: "100%",
    "& .MuiLinearProgress-colorPrimary": {
      backgroundColor: "transparent",
      height: "12px",
    },
    "& .MuiLinearProgress-barColorPrimary": {
      backgroundColor: "#7FBF7D",
      height: "12px",
    },
  },
})

const IeltsEliteProgressiveForm: FunctionComponent<PageProps> = ({
  isLoggedIn,
  loggedInNumber,
  formJson,
  prefilledData,
  onSubmitForm,
  isModal,
  formHeading,
  buttonCta,
  isMcFlow = false,
}) => {
  const classes = ieltsEliteDemoFormUseStyles()
  const router = useRouter()
  const [currentFormStep, setCurrentFormStep] = useState(1)
  const [selectedCountry, setSelectedCountry] = useState<string>("")
  const [duplicateFormValues, setDuplicateFormValues] = useState<any>({})
  const [formValues, setFormValues] = useState<any>({})
  const [isClickingBack, setIsClickingBack] = useState<boolean>(false)
  const delayForStepChange = useRef<number>(0)
  const {
    formStage,
    slotId,
    indexSlot,
    isDisqualifiedUser,
    leadSource: leadSourceFromQuery,
  } = router.query
  const leadSource = Array.isArray(leadSourceFromQuery)
    ? leadSourceFromQuery[0]
    : leadSourceFromQuery
  const isDisqualifiedUserFlow = isDisqualifiedUser === "true"
  const formDetailsObjectFromQuery: Record<string, any> =
    getFilteredFormDataFromQueryObject(router.query)
  const stage = useSelector((state: State) => state.spotCounselling.stage)

  useEffect(() => {
    if (formStage === "2") {
      setCurrentFormStep((prev) => prev + 1)
    }
  }, [formStage])
  const { isMasterclassAlias } = useDemoAlias()

  //Autonext logic
  useEffect(() => {
    autonextDecision(
      duplicateFormValues,
      formJson,
      currentFormStep,
      setCurrentFormStep,
      isClickingBack,
    )
    return () => {
      clearTimeout(delayForStepChange.current)
    }
  }, [duplicateFormValues])

  useEffect(() => {
    forceAutonextDecision(
      formJson,
      currentFormStep,
      formValues,
      setCurrentFormStep,
      isClickingBack,
    )

    if (currentFormStep === 2) {
      trackIeltsClick(
        isMasterclassAlias
          ? ieltsPageName.DEMO_MC_FORM_1
          : ieltsPageName.IELTS_ELITE_INPAGE_FORM_1,
        {
          contentName: "Next ",
          contentFormat: "Button",
          widgetName: "Inpage Form 1",
          widgetFormat: "Button",
        },
      )
      trackPageV2(
        isMasterclassAlias
          ? ieltsPageName.DEMO_MC_FORM_2
          : ieltsPageName.IELTS_ELITE_INPAGE_FORM_2,
      )
    }
  }, [currentFormStep])

  useEffect(() => {
    if (currentFormStep == 1)
      trackPageV2(
        isMasterclassAlias
          ? ieltsPageName.DEMO_MC_FORM_1
          : ieltsPageName.IELTS_ELITE_INPAGE_FORM_1,
        {
          optimizeSelection: "Base",
        },
      )
    else
      trackPageV2(
        isMasterclassAlias
          ? ieltsPageName.DEMO_MC_FORM_2
          : ieltsPageName.IELTS_ELITE_INPAGE_FORM_2,
        {
          optimizeSelection: "Base",
        },
      )
  }, [])

  useEffect(() => {
    if (isDisqualifiedUserFlow) {
      trackPageV2(ieltsPageName.IELTS_FORM_POST_BOOKING)
    }
  }, [isDisqualifiedUserFlow])

  const getCurrentFormId = (): ieltsFormIds =>
    getFormId(!!isDisqualifiedUser, isMasterclassAlias)

  const handleSaveData = async (values: any) => {
    const domainName = geturlToRedirect()
    const utmDetails = getUtmData(null)
    const newValues = cloneDeep(values)
    newValues.utmSource = utmDetails?.utmSource || ""
    newValues.utmCampaign = utmDetails?.utmCampaign || ""
    newValues.gclid = utmDetails?.gclid || ""
    newValues.adName = utmDetails?.adName || ""
    newValues.utmMedium = utmDetails?.utmMedium || ""
    newValues.utmTerm = utmDetails?.utmTerm || ""
    newValues.campaignType = utmDetails?.campaignType || ""
    newValues.adId = utmDetails?.adId || ""
    newValues.formId = getCurrentFormId()
    newValues.leadSource = leadSource ?? LeadSources.OTHER
    newValues.alias = router.query[demoAliasQueryConstant] as string

    if (newValues?.name) {
      newValues.name = nameExtraSpacetriming(newValues?.name)
    }
    if (newValues?.howLeapHelp) {
      newValues.howLeapHelp = newValues.howLeapHelp.join(",")
    }

    const utmObject: any = {
      utm_campaign: utmDetails?.utmCampaign,
      gclid: utmDetails?.gclid,
      adName: utmDetails?.adName,
      utm_source: utmDetails?.utmSource,
      utm_term: utmDetails?.utmTerm,
      utm_medium: utmDetails?.utmMedium,
      campaign_type: utmDetails?.campaignType,
      ad_id: utmDetails?.adId,
    }

    if (loggedInNumber) {
      newValues.phone = loggedInNumber
    }
    if (newValues.whatCareerStage === "WORKING") {
      await fireTagEvent("Form Submitted Working", {
        ...newValues,
        ...utmObject,
      })
      await trackIeltsFormAsync("Form Submitted Working", {
        ...newValues,
        ...utmObject,
      })
    }
    await trackIeltsFormAsync("Form Submitted", { ...newValues, ...utmObject })
    handleFormSubmissionGaEventForm(
      { ...newValues, stage },
      {
        formType: "redirect",
        formId:
          leadSource === LeadSources?.MARKETING_DEMO_MC
            ? "Ielts Demo MC Form"
            : "Ielts Demo Form",
      },
    )
    const formValuesString = getQueryParamStringFromObject(newValues)

    const redirectURL = `${domainName}/${navRoutes.IELTS_DEMO_QL_PAGE}?prefilled_phone=${newValues.phone}&flowType=Elite Demo`

    const utmString = getQueryParamStringFromObject(utmObject)
    const finalString = redirectURL + utmString + formValuesString
    redirectOutFromIframe(finalString)
  }

  const savePersonalInfo = async (values: Record<string, any>) => {
    if (currentFormStep == formJson?.page_distribution?.length) {
      const saveValue = { ...prefilledData, ...values }
      if (formJson?.page_distribution?.length == 1) {
        trackIeltsClick(
          isMasterclassAlias
            ? ieltsPageName.DEMO_MC_FORM_1
            : ieltsPageName.IELTS_ELITE_INPAGE_FORM_1,
          {
            contentName: "Next ",
            contentFormat: "Button",
            widgetName: "Inpage Form 1",
            widgetFormat: "Button",
          },
        )
      } else
        trackIeltsClick(
          isMasterclassAlias
            ? ieltsPageName.DEMO_MC_FORM_2
            : ieltsPageName.IELTS_ELITE_INPAGE_FORM_2,
          {
            contentName: "Next",
            contentFormat: "Button",
            widgetName: "Inpage Form 2",
            widgetFormat: "Button",
          },
        )
      if (onSubmitForm) onSubmitForm(saveValue)
      else handleSaveData(saveValue)
    } else {
      setCurrentFormStep((step) => step + 1)
    }
  }

  const handleRedirctingAB = async (values: any) => {
    const domainName = geturlToRedirect()
    const utmDetails = getUtmData(null)
    const newValues = cloneDeep(values)
    newValues.utmSource = utmDetails?.utmSource || ""
    newValues.utmCampaign = utmDetails?.utmCampaign || ""
    newValues.gclid = utmDetails?.gclid || ""
    newValues.adName = utmDetails?.adName || ""
    newValues.utmMedium = utmDetails?.utmMedium || ""
    newValues.utmTerm = utmDetails?.utmTerm || ""
    newValues.campaignType = utmDetails?.campaignType || ""
    newValues.adId = utmDetails?.adId || ""
    newValues.formId = getCurrentFormId()
    newValues.leadSource = leadSource ?? LeadSources.OTHER

    if (newValues?.name) {
      newValues.name = nameExtraSpacetriming(newValues?.name)
    }
    if (newValues?.howLeapHelp) {
      newValues.howLeapHelp = newValues.howLeapHelp.join(",")
    }

    if (currentFormStep == 2) {
      trackIeltsClick(ieltsPageName.IELTS_ELITE_INPAGE_FORM_2, {
        contentName: "Next",
        contentFormat: "Button",
        widgetName: "Inpage Form 2",
        widgetFormat: "Button",
      })
    } else {
      trackIeltsClick(ieltsPageName.IELTS_ELITE_INPAGE_FORM_1, {
        contentName: "Next ",
        contentFormat: "Button",
        widgetName: "Inpage Form 1",
        widgetFormat: "Button",
      })
    }

    const utmObject: any = {
      utm_campaign: utmDetails?.utmCampaign,
      utm_source: utmDetails?.utmSource,
      utm_term: utmDetails?.utmTerm,
      utm_medium: utmDetails?.utmMedium,
      gclid: utmDetails?.gclid,
      adName: utmDetails?.adName,
      campaign_type: utmDetails?.campaignType,
      ad_id: utmDetails?.adId,
    }

    if (slotId)
      await trackIeltsFormAsync("Form Submitted", {
        ...newValues,
        ...utmObject,
      })

    const formValuesString = getQueryParamStringFromObject(newValues)

    const redirectURL = slotId
      ? `${domainName}/${
          navRoutes.IELTS_ELITE_SLOTS_SCREEN
        }?flowType=Elite Demo&formStage=${
          formStage || "1"
        }&slotId=${slotId}&indexSlot=${indexSlot}&abTest=true`
      : `${domainName}/${
          navRoutes.IELTS_ELITE_SLOTS_SCREEN
        }?flowType=Elite Demo&formStage=${formStage || "1"}&abTest=true`

    const disqualifiedRedirectUrl = slotId
      ? `${domainName}/${navRoutes.IELTS_DEMO_FLOW_FOR_DISQUALIFIED_USERS}?flowType=Elite Demo&formStage=2&slotId=${slotId}&indexSlot=${indexSlot}&changeHasBooked=true`
      : `${domainName}/${navRoutes.IELTS_DEMO_FLOW_FOR_DISQUALIFIED_USERS}?flowType=Elite Demo&formStage=2&changeHasBooked=true`

    const utmString = getQueryParamStringFromObject(utmObject)
    const finalString =
      (isDisqualifiedUser ? disqualifiedRedirectUrl : redirectURL) +
      utmString +
      formValuesString

    if (formStage === "2") redirectOutFromIframe(finalString)
    else window.open(finalString, "_self")
  }

  const savePersonalInfoForABTest = async (values: Record<string, any>) => {
    if (currentFormStep == formJson?.page_distribution?.length) {
      const saveValue = {
        ...prefilledData,
        ...values,
        ...formDetailsObjectFromQuery,
      }
      if (isDisqualifiedUserFlow) {
        trackIeltsClick(ieltsPageName.IELTS_FORM_POST_BOOKING, {
          contentName: "Submit",
        })
      }

      handleRedirctingAB(saveValue)
    } else {
      handleRedirctingAB(values)
    }
  }

  const getCtaText = useCallback(() => {
    if (currentFormStep) {
      if (currentFormStep == formJson?.page_distribution?.length) {
        if (buttonCta) return buttonCta
        if (isDisqualifiedUser) return "Confirm Your Slot"
        return "Select MasterClass Slot"
      } else {
        return "NEXT"
      }
    }
  }, [currentFormStep])

  const RenderFields = ({
    component_type,
    question_text,
    options,
    validation,
    fieldValue,
    label,
    default_visibility,
    visibility_dependency,
    values,
    numGridColumns,
    question_id,
    isMultiSelect = false,
    maxSelect = 1,
    fetchSuggestions,
    defaultSuggestions,
  }: any) => {
    let isThisFieldVisible = true
    for (let i = 0; i < visibility_dependency?.dependencies?.length; i++) {
      if (
        visibility_dependency?.dependencies[i]?.value?.includes(
          values[
            getDependentQuestionFieldName(
              formJson,
              visibility_dependency?.dependencies[i]?.question_id,
            )
          ],
        )
      ) {
        continue
      } else {
        isThisFieldVisible = false
        break
      }
    }
    if (isThisFieldVisible || default_visibility)
      switch (component_type) {
        case "single_select_tile": {
          return (
            <>
              {question_id === 1 && (
                <div className="mb-6">
                  <div className="my-4">
                    {formHeading ? (
                      <div>{formHeading}</div>
                    ) : (
                      <HeadingIeltsElite />
                    )}
                  </div>
                  {!isModal && (
                    <hr className="bg-[#EDEEFF] border-[#EDEEFF] md:min-w-[100vw] md:ml-[-28vw] border-[1px] mt-12" />
                  )}
                </div>
              )}
              <p
                className={`text-center text-gray-170 md:text-font24 font-bold ${
                  question_id === 1 && !isMcFlow ? "pt-[48px]" : null
                }`}
              >
                {question_text}
              </p>
              <div className={"md:px-7 md:py-2 px-1 py-2"}>
                <Field
                  name={fieldValue}
                  label={label}
                  component={CustomInputSelect}
                  options={options}
                  isMultiSelect={isMultiSelect}
                  maxSelect={maxSelect}
                  numGridColumns={numGridColumns ?? null}
                  validate={(v: any) => {
                    if (fieldValue === "preferredCountry") setSelectedCountry(v)
                    if (formJson?.required.includes(fieldValue) && !v)
                      return "This is a mandatory field"
                    if (v && isMultiSelect)
                      return validateHowToHelpVal(v, maxSelect)
                  }}
                  onClick={(e: any) => {
                    setIsClickingBack(false)
                    if (!isMultiSelect && formJson?.is_auto_progressive) {
                      delayForStepChange.current = window.setTimeout(() => {
                        setDuplicateFormValues({
                          ...duplicateFormValues,
                          [fieldValue]: e?.target?.value,
                        })
                      }, 200)
                    }
                  }}
                  nudgeOption={
                    formJson?.intakeReccomendationNudge?.filter(
                      (elem: any) => elem.country === values.preferredCountry,
                    )[0]
                  }
                />
              </div>
            </>
          )
        }
        case "searchInput": {
          return (
            <>
              <p
                className={`text-center text-gray-170 md:text-font24 font-bold ${
                  question_id === 1 ? "pt-[48px]" : null
                }`}
              >
                {question_text}
              </p>
              <div className={"md:px-[36px] md:py-2 px-3 py-2"}>
                <Field
                  name={fieldValue}
                  component={SearchDropDown}
                  fetchSuggestions={fetchSuggestions}
                  defaultSuggestions={defaultSuggestions}
                  placeholder={label}
                  validate={(v) => {
                    if (!v) return "Please select a city"
                  }}
                  handleInputChange={(val: string) => {
                    trackEventAsync("Click", {
                      contentName: "Ielts City Search",
                      city: val,
                      formid: "IELTS Demo Form",
                    })
                  }}
                />
              </div>
            </>
          )
        }

        case "numberInput": {
          return (
            <>
              <p
                className={`text-center text-gray-170 md:text-font24 font-bold ${
                  question_id === 1 ? "pt-[48px]" : null
                }`}
              >
                {question_text}
              </p>
              <div className="md:px-7 md:py-2 px-4 py-2 w-full md:w-4/5 md:mx-auto">
                <Field
                  name={fieldValue}
                  component={InputField}
                  placeholder={label}
                  label={"label"}
                  type="number"
                  validate={(v) => {
                    if (formJson?.required.includes(fieldValue) && !v)
                      return "This is a mandatory field"
                    if (v && validation.pattern) {
                      const regex = new RegExp(validation?.pattern)
                      if (!regex.test(v)) return validation.errorMessage
                    }
                  }}
                />
              </div>
            </>
          )
        }

        case "textInput": {
          if ((fieldValue === "phone" && !isLoggedIn) || fieldValue !== "phone")
            return (
              <>
                {question_id === 4 && (
                  <div className="mb-6">
                    <div className="my-4">
                      {formHeading ? null : <HeadingIeltsElite />}
                    </div>
                    {!isModal && (
                      <hr className="bg-[#EDEEFF] border-[#EDEEFF] md:min-w-[100vw] md:ml-[-28vw] border-[1px] mt-12" />
                    )}
                  </div>
                )}

                <p
                  className={`text-center text-gray-170 md:text-font24 font-bold ${
                    question_id === 1 ? "pt-[48px]" : null
                  }`}
                >
                  {question_text}
                </p>
                <div
                  className={`${question_id === 11 ? "md:mt-4 mt-2" : null}`}
                >
                  <div className="md:px-7 md:py-2 px-4 py-2 my-4 w-full md:w-4/5 md:mx-auto">
                    <Field
                      name={fieldValue}
                      component={InputField}
                      placeholder={label}
                      label={label}
                      type="text"
                      validate={(v) => {
                        if (formJson?.required.includes(fieldValue) && !v)
                          return "This is a mandatory field"
                        if (v && !v.trim()) return "Please enter correct answer"

                        if (v && validation.pattern) {
                          const regex = new RegExp(validation?.pattern)
                          if (!regex.test(v)) return validation.errorMessage
                        }
                      }}
                    />
                  </div>
                </div>
              </>
            )
        }
      }
  }
  return (
    <div className="relative">
      <div
        className={`${styles.wrapperLeadCapture} ${
          isMcFlow ? "!min-h-[512px]" : ""
        } `}
      >
        <div className={`${classes.root}`}>
          {currentFormStep == 2 &&
            formJson?.required?.includes("preferredCountry") && (
              <div className="bg-primary py-4">
                <p className="text-center text-white text-sm font-bold">
                  {`${
                    intakeValueToLabel[
                      formJson?.intakeReccomendationNudge?.find(
                        (elem: any) => elem.country === selectedCountry,
                      ).intake
                    ]
                  } has a higher acceptance rate`}
                </p>
              </div>
            )}
          {!isMcFlow && (
            <LinearProgress
              variant="determinate"
              value={Math.round(
                (currentFormStep / formJson?.page_distribution?.length) * 100,
              )}
            />
          )}
        </div>
        {currentFormStep > 1 && (
          <div
            className="max-w-row mx-auto text-gray-1741 flex items-center pt-4 md:pl-0 pl-2 cursor-pointer"
            onClick={() => {
              setCurrentFormStep((step) => step - 1)
              setDuplicateFormValues({})
              setIsClickingBack(true)
            }}
          >
            <img
              src={`${process.env.CDN_URL}/assets/icons/back_arrow1.svg`}
              alt=""
              width="30px"
              height="30px"
            />
            <p className="text-font16 hidden md:inline-block pl-2">BACK</p>
          </div>
        )}
        <div
          className={`flex flex-col justify-between ${
            styles.wrapperLeadCapture
          } ${isMcFlow ? "!min-h-[512px] py-6" : ""}`}
        >
          <div className={styles.fieldsContainer}>
            <Form
              onSubmit={
                isDisqualifiedUserFlow
                  ? savePersonalInfoForABTest
                  : savePersonalInfo
              }
              initialValues={prefilledData}
            >
              {({ handleSubmit, values, submitting }) => {
                setFormValues(values)

                return (
                  <div>
                    <form onSubmit={handleSubmit} noValidate>
                      {formJson?.fields
                        .slice(
                          indexCal(formJson, currentFormStep)[0],
                          indexCal(formJson, currentFormStep)[1],
                        )
                        .map((field: any) => {
                          const {
                            type,
                            options,
                            question_text,
                            fieldValue,
                            label,
                            validation,
                            default_visibility,
                            visibility_dependency,
                            numGridColumns,
                            question_id,
                            isMultiSelect,
                            maxSelect,
                            fetchSuggestions,
                            defaultSuggestions,
                          } = field
                          return RenderFields({
                            component_type: type,
                            question_text,
                            options,
                            validation,
                            // onClick,
                            fieldValue,
                            label,
                            default_visibility,
                            visibility_dependency,
                            values,
                            numGridColumns,
                            question_id,
                            isMultiSelect,
                            maxSelect,
                            fetchSuggestions,
                            defaultSuggestions,
                          })
                        })}
                    </form>
                    <div className="w-full flex justify-center mt-6">
                      <button
                        className="bg-primary py-3 rounded text-white flex justify-center w-4/5 md:w-3/5 mx-auto my-4 items-center"
                        onClick={() => {
                          handleSubmit()
                        }}
                      >
                        <p className="pr-2 md:text-font16 font-bold">
                          {submitting ? "SAVING..." : getCtaText()}
                        </p>
                        {!submitting && (
                          <img
                            src={`${process.env.CDN_URL}/assets/icons/practice_tab/whitearrow.svg`}
                            className="scale-125"
                          />
                        )}
                      </button>
                    </div>
                  </div>
                )
              }}
            </Form>
          </div>
          <img
            src={`${process.env.CDN_URL}/assets/images/lead-capture/background-illustration.svg`}
            className="bottom-0 hidden md:block -z-1 md:absolute"
          />
          {!isMcFlow && (
            <img
              src={`${process.env.CDN_URL}/assets/images/lead-capture/background-illustration-mobile.svg`}
              className="bottom-0 md:hidden w-full -z-1"
            />
          )}
        </div>
      </div>
    </div>
  )
}

export default IeltsEliteProgressiveForm
